import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Regulamin() {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>Regulamin | ApisAI</title>
        <meta name="description" content="Regulamin serwisu ApisAI - platforma promocji hodowli psów i kotów." />
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <span className="font-semibold text-white">Regulamin</span>
          </div>
        </nav>

        {/* Content */}
        <main className="pt-24 pb-16 px-6">
          <div className="max-w-3xl mx-auto prose prose-invert prose-gray">
            <h1 className="text-3xl font-bold text-white mb-8">Regulamin serwisu ApisAI</h1>
            
            <p className="text-gray-400 mb-6">Ostatnia aktualizacja: 24 stycznia 2026</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">§1. Postanowienia ogólne</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Niniejszy Regulamin określa zasady korzystania z serwisu ApisAI dostępnego pod adresem apisai.pl.</li>
                <li>Serwis ApisAI umożliwia hodowcom psów i kotów tworzenie profesjonalnych stron internetowych dla swoich hodowli i miotów.</li>
                <li>Właścicielem serwisu jest ApisAI.</li>
                <li>Korzystanie z serwisu oznacza akceptację niniejszego Regulaminu.</li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">§2. Definicje</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><strong>Serwis</strong> — platforma internetowa ApisAI.</li>
                <li><strong>Użytkownik</strong> — osoba fizyczna lub prawna korzystająca z Serwisu.</li>
                <li><strong>Konto</strong> — indywidualne konto Użytkownika w Serwisie.</li>
                <li><strong>Hodowla</strong> — profil hodowli utworzony przez Użytkownika.</li>
                <li><strong>Miot</strong> — strona prezentująca szczenięta/kocięta do sprzedaży.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">§3. Rejestracja i Konto</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Rejestracja w Serwisie jest bezpłatna.</li>
                <li>Użytkownik zobowiązuje się do podania prawdziwych danych podczas rejestracji.</li>
                <li>Użytkownik jest odpowiedzialny za bezpieczeństwo swojego hasła.</li>
                <li>Jedno konto może być używane tylko przez jednego Użytkownika.</li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">§4. Plany i płatności</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Serwis oferuje plany: FREE (bezpłatny), PRO i PREMIUM (płatne).</li>
                <li>Płatności są obsługiwane przez zewnętrznego operatora płatności (Stripe).</li>
                <li>Subskrypcje są odnawiane automatycznie, chyba że zostaną anulowane.</li>
                <li>Użytkownik może anulować subskrypcję w dowolnym momencie.</li>
                <li>Po anulowaniu subskrypcji dostęp do funkcji płatnych jest zachowany do końca okresu rozliczeniowego.</li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">§5. Treści Użytkownika</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Użytkownik zachowuje prawa autorskie do treści, które publikuje w Serwisie.</li>
                <li>Użytkownik udziela Serwisowi licencji na publikację i wyświetlanie treści.</li>
                <li>Zabrania się publikowania treści niezgodnych z prawem, obraźliwych lub wprowadzających w błąd.</li>
                <li>Serwis zastrzega sobie prawo do usunięcia treści naruszających Regulamin.</li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">§6. Odpowiedzialność</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Serwis nie ponosi odpowiedzialności za treści publikowane przez Użytkowników.</li>
                <li>Serwis nie gwarantuje pozycji w wynikach wyszukiwania Google lub systemach AI.</li>
                <li>Serwis dokłada starań, aby zapewnić ciągłość działania, ale nie gwarantuje 100% dostępności.</li>
              </ol>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">§7. Postanowienia końcowe</h2>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Serwis zastrzega sobie prawo do zmiany Regulaminu.</li>
                <li>O zmianach Użytkownicy będą informowani drogą elektroniczną.</li>
                <li>W sprawach nieuregulowanych stosuje się przepisy prawa polskiego.</li>
                <li>Spory rozstrzygane będą przez sąd właściwy dla siedziby Serwisu.</li>
              </ol>
            </section>

            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-gray-500 text-sm">
                W przypadku pytań dotyczących Regulaminu, prosimy o kontakt: kontakt@apisai.pl
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8">
          <div className="max-w-4xl mx-auto px-6 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <Link to="/regulamin" className="hover:text-white transition-colors">Regulamin</Link>
            <Link to="/polityka-prywatnosci" className="hover:text-white transition-colors">Polityka prywatności</Link>
            <Link to="/polityka-cookies" className="hover:text-white transition-colors">Cookies</Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link>
          </div>
        </footer>
      </div>
    </>
  );
}
